import "ag-grid-enterprise"

import {
  DEFAULT_CUBE_FD_SEGMENT,
  GMP_VIEW_TYPE,
  YEAR_SCENARIO_OPTIONS,
  getDefaultView,
  getFilterKey,
  getFilterKeys,
  getFilterValue,
  getQueryByView,
  viewOptions,
} from "./config"
import {
  DEFAULT_CUBE_PAGE_LIMIT,
  DEFAULT_SCROLL_DEBOUNCE,
} from "../../constants"
import { GridOptions, LicenseManager } from "ag-grid-enterprise"
import { RefObject, useCallback, useEffect, useMemo, useState } from "react"
import {
  ReportPayload,
  excelStyles,
  getColumnDefs,
  getNodeRoute,
} from "../../utils"

import { AgGridReact } from "ag-grid-react"
import { Box } from "@material-ui/core"
import LoadTemplateModal from "../LoadTemplateModal"
import { Loading } from "components/Loading"
import MultipleSelect from "./MultipleSelect"
import SaveTemplateModal from "../SaveTemplateModal"
import SegmentSelect from "./SegmentSelect"
import ViewSelect from "./ViewSelect"
import { ViewType } from "./models"
import { dataSource } from "../../api"
import { proxy } from "valtio"
import { useAuthenticated } from "providers/A12n"
import { useCubeMeta } from "@cubejs-client/react"
import { useGetUserInfo } from "routes/Base/Dashboards/RevenueDashboard/hooks/useGetUserInfo"
import { useRevenueParams } from "routes/Base/Dashboards/RevenueDashboard/hooks/useRevenueParams"

LicenseManager.setLicenseKey(
  "Using_this_AG_Grid_Enterprise_key_( AG-042876 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Logicea, LLC )_is_granted_a_( Single Application )_Developer_License_for_the_application_( Halogen )_only_for_( 1 )_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_( Halogen )_need_to_be_licensed___( Halogen )_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 23 May 2024 )____[v2]_MTcxNjQxODgwMDAwMA==05ac1de5389eeee14509def26b01a8e5",
)

export const modalStates = proxy({
  saveTemplate: { open: false },
  loadTemplate: { open: false },
})

const style = { height: "80vh", width: "100%" }

type Props = {
  gridRef?: RefObject<AgGridReact>
  modalStates: {
    saveTemplate: {
      open: boolean
    }
    loadTemplate: {
      open: boolean
    }
  }
  resetState: () => void
  setExportPayload: (value: ReportPayload) => void
}

const gridOptions: GridOptions = {
  defaultColDef: {
    flex: 1,
    minWidth: 180,
    sortable: true,
    resizable: true,
  },
  sideBar: {
    toolPanels: [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
          suppressPivotMode: true,
          suppressValues: true,
          contractColumnSelection: true,
        },
      },
      "filters",
    ],
    defaultToolPanel: "columns",
  },
  suppressFieldDotNotation: true,
  rowModelType: "serverSide",
  cacheBlockSize: DEFAULT_CUBE_PAGE_LIMIT,
  blockLoadDebounceMillis: DEFAULT_SCROLL_DEBOUNCE,
  excelStyles,
}

export const FDReportingGrid = (props: Props) => {
  const { gridRef, modalStates, resetState, setExportPayload } = props
  const { meta } = useAuthenticated()

  const {
    getParam,
    hasParam,
    isGCFParam,
    yearParam,
    scenarioParam,
    isStream,
    brandParam,
    clientEnabled,
    search: urlParams,
  } = useRevenueParams()

  const { isGmpUser } = useGetUserInfo()

  const [type, setType] = useState<ViewType>(
    getDefaultView(isGCFParam, clientEnabled),
  )

  const [hasToApplyURLFilters, setHasToApplyURLFilters] = useState(!!urlParams)
  const [selectedSegment, setSelectedSegment] = useState(
    DEFAULT_CUBE_FD_SEGMENT,
  )
  const [isGridEnabled, setIsGridEnabled] = useState(false)

  const [selectedYearsScenarios, setSelectedYearsScenarios] = useState<{
    years: string[]
    scenarios: string[]
  }>({
    years: [hasParam("year") ? yearParam : "2024"],
    scenarios: [hasParam("scenario") ? scenarioParam : "BUD"],
  })

  const { timeZone = "Etc/UTC" } = meta ?? {}
  const cubeMeta = useCubeMeta()
  const metaCubes = cubeMeta?.response?.cubes
  const data = getQueryByView(
    type.view,
    metaCubes,
    selectedSegment,
    selectedYearsScenarios,
  )

  const extraInfos = data.extraInfos
  const { segmentOptions } = extraInfos

  const query = data.query

  const isLoading = !metaCubes

  const onGridReady = useCallback(
    (query) => {
      const gridApi = gridOptions.api as any
      const hasToRemoveFirstRow = true

      const ds = dataSource(
        query,
        timeZone,
        (params) =>
          setExportPayload({
            params,
            query,
          }),
        metaCubes,
        hasToRemoveFirstRow,
      )

      gridApi?.setServerSideDatasource(ds)

      if (gridApi.destroyCalled) {
        return
      }

      if (!!urlParams && hasToApplyURLFilters) {
        getFilterKeys(type.view, !!urlParams, hasParam, isStream).map(
          (enabledFilter) =>
            gridApi
              ?.getFilterInstance(
                `${type.view}.${getFilterKey(enabledFilter, brandParam)}`,
              )
              ?.setModel({
                values: [getFilterValue(enabledFilter, hasParam, getParam)],
              })
              .then(() => gridApi?.onFilterChanged()),
        )
      }
    },
    [
      timeZone,
      metaCubes,
      urlParams,
      hasToApplyURLFilters,
      setExportPayload,
      type.view,
      hasParam,
      isStream,
      brandParam,
      getParam,
    ],
  )

  const onChangeFilters = useCallback(() => {
    gridOptions.api?.refreshServerSide({ purge: false })
    gridRef?.current?.api.forEachNode(function (node) {
      if (node.group) {
        const route = getNodeRoute(node)
        gridOptions.api?.refreshServerSide({
          route: route,
          purge: false,
        })
      }
    })
  }, [gridRef])

  const onChangeView = useCallback((value) => {
    setType(value)
    setIsGridEnabled(false)
  }, [])

  const onChangeYearsScenarios = useCallback((property, value) => {
    setSelectedYearsScenarios((prev) => ({ ...prev, [property]: value }))
    setIsGridEnabled(false)
  }, [])

  const onChangeSegment = useCallback((value) => {
    setSelectedSegment(value)
    setIsGridEnabled(false)
  }, [])

  const onLoadTemplate = useCallback(
    (type: ViewType, selectedYears: string[], selectedScenarios: string[]) => {
      setHasToApplyURLFilters(false)
      onChangeView(type)
      onChangeYearsScenarios("scenarios", selectedScenarios)
      onChangeYearsScenarios("years", selectedYears)
    },
    [onChangeView, onChangeYearsScenarios],
  )

  const extraActions = useMemo(() => {
    return (
      <Box
        display="flex"
        marginBottom="24px"
        style={{
          gap: "32px",
          opacity: isGridEnabled ? 1 : 0.4,
          pointerEvents: isGridEnabled ? "all" : "none",
        }}
      >
        <ViewSelect
          title="Type"
          options={viewOptions}
          value={isGmpUser ? "gcf" : type.key}
          onChangeView={onChangeView}
          isGmpUser={isGmpUser}
        />
        <Box width="256px" height="48px">
          <SegmentSelect
            onChangeSegment={onChangeSegment}
            defaultSegment={selectedSegment}
            segmentOptions={segmentOptions}
          />
        </Box>
        <MultipleSelect
          label="Years"
          value={selectedYearsScenarios.years}
          options={YEAR_SCENARIO_OPTIONS.years}
          onChangeParent={(value) => onChangeYearsScenarios("years", value)}
          disabled={!isGridEnabled}
        />
        <MultipleSelect
          label="Scenarios"
          value={selectedYearsScenarios.scenarios}
          options={YEAR_SCENARIO_OPTIONS.scenarios}
          onChangeParent={(value) => onChangeYearsScenarios("scenarios", value)}
          disabled={!isGridEnabled}
        />
      </Box>
    )
  }, [
    isGridEnabled,
    isGmpUser,
    type.key,
    onChangeView,
    onChangeSegment,
    selectedSegment,
    segmentOptions,
    selectedYearsScenarios.years,
    selectedYearsScenarios.scenarios,
    onChangeYearsScenarios,
  ])

  useEffect(() => {
    if (isGmpUser) {
      setType(GMP_VIEW_TYPE)
    }
  }, [isGmpUser])

  if (isLoading) {
    return <Loading />
  }

  const columnDefs = getColumnDefs(query, timeZone, metaCubes, true)

  return (
    <div className="ag-theme-alpine" style={style}>
      {extraActions}
      <AgGridReact
        key={`${selectedYearsScenarios.scenarios}-${selectedYearsScenarios.years}-${type.key}-${selectedSegment}`}
        gridOptions={gridOptions}
        ref={gridRef}
        onGridReady={() => onGridReady(query)}
        onFilterChanged={onChangeFilters}
        columnDefs={columnDefs}
        getRowId={(params) => {
          const data = params.data
          const rowID = (query.dimensions || [])
            .filter((key) => key in data)
            .reduce((id, key) => `${data[key]}-${id}`, "")
          return rowID
        }}
        onFirstDataRendered={() => setIsGridEnabled(true)}
      />
      <SaveTemplateModal
        onClose={() => (modalStates.saveTemplate.open = false)}
        gridRef={gridRef!}
        additionalConfig={{
          fdAdditionalConfig: {
            type,
            selectedScenarios: selectedYearsScenarios.scenarios,
            selectedYears: selectedYearsScenarios.years,
          },
        }}
      />
      <LoadTemplateModal
        onClose={() => (modalStates.loadTemplate.open = false)}
        resetState={resetState}
        gridRef={gridRef!}
        onLoadFDTemplate={onLoadTemplate}
      />
    </div>
  )
}
